import {Icon} from '@iconify/react';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import CloseRoomModal from '../../../components/widget/CloseRoomModal';
import ExitLobbyModal from '../../../components/widget/ExitLobbyModal';
import FileImport from '../../../components/widget/FileImport';
import LogoutModal from '../../../components/widget/LogoutModal';
import {useLayout} from '../../../hooks/useLayout';
import Button from '../../../incase-domain/components/button/Button';
import DrawerMenu from '../../../incase-domain/components/drawerMenu/DrawerMenu';
import LanguageSelector from '../../../incase-domain/widgets/languageSelector/LanguageSelector';
import {openEndPresentationAction} from '../../../store/actions/chalkBoardElements.actions';
import {toggleDrawEnabledAction} from '../../../store/actions/dashboard.actions';
import {setDesktopViewSelected} from '../../../store/actions/layout.actions';
import {removeCurrentSlideDrawingAction} from '../../../store/actions/runfile.actions';
import {loggedInUserSelector} from '../../../store/selectors/auth.selector';
import {desktopViewSelector} from '../../../store/selectors/layout.selectors';
import {drawEnabledSelector, loadedPresentationSelector} from '../../../store/selectors/presentation.selectors';
import {usePresentationMode} from '../../../hooks/usePresentationMode';
import Modal from './Modal';

interface DrawerProps {
    hasPresActions?: boolean;
    inLobby?: boolean;
}

function HeaderMenu({hasPresActions, inLobby = false}: DrawerProps): JSX.Element {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [openDrawer, setOpenDrawer] = useState(false);
    const [openLogoutModal, setOpenLogoutModal] = useState(false);
    const [lobbyExitModal, setLobbyExitModal] = useState(false);
    const [openLanguageModal, setOpenLanguageModal] = useState(false);

    const loggedInUser = useSelector(loggedInUserSelector);
    const presentation = useSelector(loadedPresentationSelector);
    const isDesktopViewSelected = useSelector(desktopViewSelector);
    const drawEnabled = useSelector(drawEnabledSelector);
    const {exitModalVariant} = usePresentationMode();

    const {mobileScreenSize} = useLayout();

    return (
        <>
            <div className="c-headerMenuWrapper">
                <Button leading="solar:hamburger-menu-linear" size="xl" variant="ghost" square onClick={() => setOpenDrawer(true)} />

                <DrawerMenu open={openDrawer} setOpen={setOpenDrawer}>
                    <div className="c-headerMenu">
                        {loggedInUser && (
                            <div className="c-headerMenu__profile">
                                <Icon className="c-headerMenu__profile_avatar" icon="solar:user-bold" />

                                <div className="c-headerMenu__profile_name">{loggedInUser?.username}</div>
                                <div className="c-headerMenu__profile_email">{loggedInUser?.email}</div>
                            </div>
                        )}

                        <div className="c-headerMenu__navigation">
                            {!hasPresActions && !inLobby && <FileImport label fullWidth />}

                            {hasPresActions && presentation && mobileScreenSize && (
                                <Button
                                    className="c-headerMenu__group_button"
                                    variant="ghost"
                                    size="xl"
                                    leading={isDesktopViewSelected ? 'solar:iphone-linear' : 'solar:display-linear'}
                                    onClick={() => dispatch(setDesktopViewSelected(!isDesktopViewSelected))}
                                >
                                    {t(isDesktopViewSelected ? 'header.mobileView' : 'header.desktopView')}
                                </Button>
                            )}

                            {hasPresActions && presentation && (
                                <>
                                    <Button
                                        className="c-headerMenu__group_button"
                                        variant="ghost"
                                        size="xl"
                                        leading="streamline:pen-draw"
                                        color={drawEnabled ? 'secondary' : 'primary'}
                                        onClick={() => dispatch(toggleDrawEnabledAction())}
                                    >
                                        {t('header.draw')}
                                    </Button>
                                    <Button
                                        className="c-headerMenu__group_button"
                                        variant="ghost"
                                        size="xl"
                                        leading="solar:eraser-linear"
                                        onClick={() => {
                                            dispatch(removeCurrentSlideDrawingAction());
                                            dispatch(toggleDrawEnabledAction());
                                        }}
                                        disabled={!drawEnabled}
                                    >
                                        {t('header.erase')}
                                    </Button>
                                </>
                            )}
                            <div style={{flex: '1'}} />

                            <Button
                                className="c-headerMenu__group_button"
                                variant="ghost"
                                size="xl"
                                leading="solar:globus-linear"
                                onClick={() => setOpenLanguageModal(true)}
                            >
                                {t('header.language')}
                            </Button>

                            {loggedInUser && (
                                <Button
                                    className="c-headerMenu__group_button"
                                    variant="ghost"
                                    size="xl"
                                    leading="solar:logout-2-linear"
                                    onClick={() => setOpenLogoutModal(true)}
                                >
                                    {t('header.logout')}
                                </Button>
                            )}

                            {hasPresActions && presentation && (
                                <Button
                                    className="c-headerMenu__group_button"
                                    variant="ghost"
                                    size="xl"
                                    leading="solar:exit-linear"
                                    onClick={() => dispatch(openEndPresentationAction())}
                                >
                                    {t('header.exit')}
                                </Button>
                            )}

                            {!hasPresActions && inLobby && (
                                <Button
                                    className="c-headerMenu__group_button"
                                    variant="ghost"
                                    size="xl"
                                    leading="solar:exit-linear"
                                    onClick={() => setLobbyExitModal(true)}
                                >
                                    {t('header.exit')}
                                </Button>
                            )}
                        </div>
                    </div>
                </DrawerMenu>
            </div>

            <LogoutModal isOpen={openLogoutModal} setOpen={setOpenLogoutModal} />

            {exitModalVariant === 'closeRoom' ? (
                <CloseRoomModal isOpen={lobbyExitModal} setOpen={setLobbyExitModal} />
            ) : (
                <ExitLobbyModal isOpen={lobbyExitModal} setOpen={setLobbyExitModal} />
            )}

            <Modal size="fit-content" isOpen={openLanguageModal} onCancel={() => setOpenLanguageModal(false)} contentOnly>
                <LanguageSelector mobile onSelect={() => setOpenLanguageModal(false)} />
            </Modal>
        </>
    );
}

export default HeaderMenu;
